import {
  cloneElement,
  ReactElement,
  useCallback,
  useRef,
  useState,
} from 'react';

export type AnimationParams = {
  keyframes: Keyframe[] | PropertyIndexedKeyframes | null;
  options?: KeyframeAnimationOptions;
};

type Child = ReactElement | 0 | '' | false | null | undefined;

export type AnimateEnterAndExitProps = {
  animate?: AnimationParams;
  animationKey?: string | number | boolean | null | symbol | undefined;
  children: Child;
  enter?: AnimationParams;
  exit?: AnimationParams;
  initial?: boolean;
  // Fired when the exit animation finishes and the old child has unmounted
  // (also fired synchronously when the browser can't animate). Lets callers
  // react to the real end of the transition instead of racing a timer.
  onExited?: () => void;
};

function getKey(child: Child) {
  return child ? child.key : undefined;
}

function getAnimation(
  element: HTMLElement,
  enterOrExit: AnimationParams | undefined,
  animate?: AnimationParams,
  animateDirection?: PlaybackDirection
) {
  // Defends against browser non-support
  if (!element.animate) {
    return;
  }
  if (enterOrExit) {
    return element.animate(
      enterOrExit.keyframes,
      enterOrExit.options || animate?.options
    );
  }
  if (animate) {
    return element.animate(animate.keyframes, {
      ...animate.options,
      direction: animateDirection,
    });
  }
}

/*
 * Expects to have one child. The child should have a key or you can pass in `animationKey` as a prop. Animation occurs when the key changes.
 */
export default function AnimateEnterAndExit({
  animate,
  animationKey,
  children,
  enter,
  exit,
  initial = true,
  onExited,
}: AnimateEnterAndExitProps) {
  // We want either a single child, or a falsy value
  // We treat all falsy values as equivalent and we return null
  const newChild = children || null;

  const animationKeyRef = useRef(animationKey);
  const [child, setChild] = useState<ReactElement | null>(newChild);
  const [isEntering, setIsEntering] = useState<boolean>(initial);
  const exitAnimationRef = useRef<Animation>(null);
  // Tracks the in-flight enter animation so a quickly-following exit can cancel
  // it — otherwise both play on the same node at once (e.g. clicking "Hide"
  // mid slide-in), leaving two concurrent WAAPI animations fighting over it.
  const enterAnimationRef = useRef<Animation>(null);
  // Held in a ref so animateOutRef's identity (and the exiting element's ref)
  // stays stable across renders even if the caller passes a fresh callback.
  const onExitedRef = useRef(onExited);
  onExitedRef.current = onExited;

  const animateInRef = useCallback((element: HTMLElement | null) => {
    if (element) {
      enterAnimationRef.current = getAnimation(element, enter, animate) ?? null;
    }
  }, []);

  const animateOutRef = useCallback(
    (element: HTMLElement | null) => {
      if (!element) {
        return;
      }
      if (enterAnimationRef.current) {
        enterAnimationRef.current.cancel();
        enterAnimationRef.current = null;
      }
      if (exitAnimationRef.current) {
        exitAnimationRef.current.finish();
        exitAnimationRef.current = null;
      }
      const animation = getAnimation(element, exit, animate, 'reverse');
      if (animation) {
        animation.onfinish = () => {
          exitAnimationRef.current = null;
          setIsEntering(true);
          setChild(newChild);
          onExitedRef.current?.();
        };
        exitAnimationRef.current = animation;
        return () => animation.cancel();
      }
      // when there is no exit animation, change state immediately instead
      setIsEntering(true);
      setChild(newChild);
      onExitedRef.current?.();
      return;
    },
    [newChild]
  );

  function animateIn(element: Child) {
    // @ts-expect-error React 19 types
    return element ? cloneElement(element, { ref: animateInRef }) : null;
  }

  function animateOut(element: Child) {
    // @ts-expect-error React 19 types
    return element ? cloneElement(element, { ref: animateOutRef }) : null;
  }

  if (
    (animationKey === undefined && // animationKey overrides any child comparison
      (!!newChild !== !!child || getKey(newChild) !== getKey(child))) ||
    animationKey !== animationKeyRef.current
  ) {
    animationKeyRef.current = animationKey;
    if (!child) {
      setIsEntering(true);
      setChild(newChild);
      return null;
    }
    return animateOut(child);
  }
  if (isEntering) {
    return animateIn(newChild);
  }
  return newChild;
}
